import Errored from '../../core/Errored';
import { Typography } from '@material-ui/core';
import GS from '../../grafsync/grafsync';
import IRIentry from '../../core/IRIentry';
import { lightSanitiseText, makeCancelable } from '../../core/utils/utils';
import LabelledSwitch from '../../core/LabelledSwitch';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import ProjectLanguageSelector from '../../core/ProjectLanguageSelector';
import TitledPanel from '../../core/TitledPanel';
import TitledTextField from '../../core/TitledTextField';
import CancelButton from '../../core/CancelButton';
import PrimaryButton from '../../core/PrimaryButton';
import { globalStyles as styles } from '../../core/style/gstheme';

class AddSuggestionNoteDialog extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            open: true,
            text: "",
            useXL: props.useXL,
            lang: this.props.defaultLang,
            processingIRI: false,
            iriTheSame: false,
            resourceIRI: "",
            labelResourceIRI: "",
            computedIRI: props.resourceIRI,
            labelComputedIRI: props.labelResourceIRI,
            uniqueIRI: props.useCounterIRI ? true : false,
            uniqueLabelIRI: props.useCounterIRI ? true : false,
            viewIRI: false,
            iriRejected: false,
            labelIriRejected: false,
            isErrored: null
        };
        this.cancellable = null;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    handleXLChange = checked => this.setState({ useXL: checked, labelIriRejected: false });

    loadIDs(resourceIRI, labelResourceIRI) {
        this.cancellable = makeCancelable(this.reserveIDs(resourceIRI, labelResourceIRI));
        return this.cancellable;
    }

    reserveIDs = async (resourceIRI, labelResourceIRI) => {
        this.setState({ processingIRI: true });
        let reservationID;
        let labelReservationID;
        if (this.props.suggestionType === "NewConceptSuggestion") {
            const isFound = await GS.reserveIRI(resourceIRI, this.props.graph, "http://www.w3.org/2004/02/skos/core#Concept");
            const exists = isFound.exists;
            if (exists) {
                this.rejectIRI(true);
                return false;
            }
            reservationID = isFound.id;
        }
        if (this.state.useXL) {
            const isLabelFound = await GS.reserveIRI(labelResourceIRI, this.props.graph, "http://www.w3.org/2008/05/skos-xl#Label");
            const labelExists = isLabelFound.exists;
            if (labelExists) {
                this.rejectLabelIRI(true);
                return false;
            }
            labelReservationID = isLabelFound.id;
        }
        return {
            reservationID,
            labelReservationID
        };
    };

    getConceptCounter() {
        const { schemeIRI, graph, counters } = this.props;
        this.cancellable = makeCancelable(GS.getCounter(schemeIRI, graph, "primary", counters.primaryCounter));
        return this.cancellable;
    }

    getLabelCounter() {
        const { schemeIRI, graph, counters } = this.props;
        this.cancellable = makeCancelable(GS.getCounter(schemeIRI, graph, "secondary", counters.secondaryCounter));
        return this.cancellable;
    }

    getIDsByCounter = async () => {
        let ids;
        let resourceIRI;
        let labelResourceIRI;
        let tries = 0;
        while (!ids || ids === false) {
            tries++;
            if (tries > 20) {
                throw new Error("Cannot get unique IRIs based on counters.");
            }
            let count;
            if (this.props.suggestionType === "NewConceptSuggestion") {
                const countData = await this.getConceptCounter().promise;
                count = countData.count;
                resourceIRI = GS.computeCounterIRI(this.props.schemeIRI, this.props.useSlashIRI, count);
            }
            if (this.state.useXL) {
                const labelCountData = await this.getLabelCounter().promise;
                const labelCount = labelCountData.count;
                if (this.props.suggestionType === "NewConceptSuggestion" && count >= labelCount) {
                    throw new Error("Counts are in conflict for concept and XL label.");
                }
                labelResourceIRI = GS.computeCounterIRI(this.props.schemeIRI, this.props.useSlashIRI, labelCount);
            }
            ids = await this.loadIDs(resourceIRI, labelResourceIRI).promise;
        }
        return {
            ids,
            resourceIRI,
            labelResourceIRI
        };
    };

    addNote = async () => {
        let resourceIRI = this.state.resourceIRI;
        let labelResourceIRI = this.state.labelResourceIRI;
        if (this.props.accepted && ["NewConceptSuggestion", "SynonymSuggestion", "LabelSuggestion"].includes(this.props.suggestionType)) {
            try {
                let ids;
                if (this.props.useCounterIRI) {
                    const counterInfo = await this.getIDsByCounter();
                    ids = counterInfo.ids;
                    resourceIRI = counterInfo.resourceIRI;
                    labelResourceIRI = counterInfo.labelResourceIRI;
                }
                else {
                    ids = await this.loadIDs(resourceIRI, labelResourceIRI).promise;
                }
                if (this.cancellable && this.cancellable.isCancelled()) return;
                if (ids === false) return;
            }
            catch (e) {
                if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
                return;
            }
        }
        const { lang, text, useXL } = this.state;
        this.setState({
            open: false,
            text: "",
            useXL: true,
            lang: this.props.defaultLang,
            uniqueIRI: this.props.useCounterIRI ? true : false,
            uniqueLabelIRI: this.props.useCounterIRI ? true : false,
            iriTheSame: false,
            processingIRI: false,
            viewIRI: false,
            iriRejected: false,
            labelIriRejected: false,
            computedIRI: "",
            labelComputedIRI: "",
            resourceIRI: "",
            labelResourceIRI: ""
        });
        if (text !== "") {
            this.props.onAddNote(lang, text, useXL, resourceIRI, labelResourceIRI);
        }
        else {
            this.props.onSkipNote(useXL, resourceIRI, labelResourceIRI);
        }
    };

    cancelAddNote = () => {
        this.setState({
            open: false,
            text: "",
            lang: this.props.defaultLang,
            uniqueIRI: this.props.useCounterIRI ? true : false,
            uniqueLabelIRI: this.props.useCounterIRI ? true : false,
            iriTheSame: false,
            processingIRI: false,
            viewIRI: false,
            iriRejected: false,
            labelIriRejected: false,
            computedIRI: "",
            labelComputedIRI: "",
            resourceIRI: "",
            labelResourceIRI: ""
        });
        this.props.onCancelNote();
    };

    selectLanguage = (lang) => this.setState({ lang });

    handleChange = event => {
        const t = event.target;
        const cleanText = lightSanitiseText(t.value.trimLeft());
        this.setState({ [t.name]: cleanText });
    };

    resetIRIconfirmation = () => this.setState({ uniqueIRI: false, iriRejected: false });

    confirmUniqueIRI = (iri) => {
        this.setState({ uniqueIRI: true, iriRejected: false, resourceIRI: iri, iriTheSame: iri === this.state.labelResourceIRI });
    };

    rejectIRI = (processingIRI = false) => this.setState({ uniqueIRI: false, iriRejected: true, processingIRI });

    resetLabelIRIconfirmation = () => this.setState({ uniqueLabelIRI: false, labelIriRejected: false });

    confirmLabelUniqueIRI = (iri) => {
        this.setState({ uniqueLabelIRI: true, labelIriRejected: false, labelResourceIRI: iri, iriTheSame: iri === this.state.resourceIRI });
    };

    rejectLabelIRI = () => this.setState({ uniqueLabelIRI: false, labelIriRejected: true, processingIRI: false });

    handleShowChange = checked => this.setState({ viewIRI: checked });

    render() {

        const { classes, optionForXL, title, accepted, graph, suggestionType, iriOptions, schemeIRI, useCounterIRI } = this.props;
        const { text, lang, useXL, computedIRI, viewIRI, isErrored,
            uniqueIRI, uniqueLabelIRI, labelComputedIRI, processingIRI, iriRejected, labelIriRejected, iriTheSame } = this.state;

        if (isErrored) return <Errored dataTest="sn-error" data={isErrored} />;

        const invalid = accepted && suggestionType === "NewConceptSuggestion" ?
            processingIRI || (useXL && iriTheSame) || !uniqueIRI || (useXL && !uniqueLabelIRI)
            :
            accepted && (suggestionType === "SynonymSuggestion" || suggestionType === "LabelSuggestion") ?
                processingIRI || (useXL && !uniqueLabelIRI)
                :
                false;

        return (
            <React.Fragment>
                <Dialog
                    data-test="add-suggestion-note-dialog"
                    maxWidth="md"
                    fullWidth
                    classes={{
                        paper: classes.paper,
                        paperWidthMd: classes.largeDialog,
                    }}
                    className={classes.dialog}
                    open={this.state.open}
                    onClose={this.cancelAddNote}
                    aria-labelledby="add-suggestion-note-title">
                    <DialogTitle
                        data-test="add-suggestion-note-title"
                        disableTypography
                        className={classes.mainHeading}
                        id="text-dialog-title">
                        {title || "Additional Information"}
                    </DialogTitle>
                    <DialogContent data-test="add-suggestion-note-dialog" style={{ overflow: 'scroll' }}>
                        <form autoComplete="off" noValidate="novalidate">
                            <TitledPanel title="Language">
                                <ProjectLanguageSelector selectedLang={lang} includeLabel={false} onSelect={this.selectLanguage} />
                            </TitledPanel>
                            <TitledTextField
                                dataTest="language-text"
                                name="text"
                                title={"Detail"}
                                lang={lang}
                                margin={true}
                                placeholder="Enter a note relating to your decision"
                                required={false}
                                text={text}
                                multiline={true}
                                rows={3}
                                maxLength={500}
                                onEnter={text.length > 0 && lang !== "" ? this.addItem : null}
                                onChange={this.handleChange} />
                            {(["NewConceptSuggestion", "SynonymSuggestion", "LabelSuggestion"].includes(suggestionType)) &&
                                <div>
                                    {accepted &&
                                        <div style={{ display: 'flex', width: '100%' }}>
                                            {optionForXL &&
                                                <LabelledSwitch
                                                    dataTest="useXL"
                                                    checked={useXL}
                                                    handleChange={this.handleXLChange}
                                                    label="Create as XL Label" />
                                            }
                                            {(suggestionType === "NewConceptSuggestion" || (optionForXL && useXL)) && !useCounterIRI &&
                                                <div style={{ flex: 1, textAlign: 'right' }}>
                                                    <LabelledSwitch
                                                        dataTest="show-iri"
                                                        checked={viewIRI}
                                                        disabled={processingIRI}
                                                        handleChange={this.handleShowChange}
                                                        label="View/Edit IRI" />
                                                </div>
                                            }
                                        </div>
                                    }
                                    {accepted &&
                                        <div>
                                            {suggestionType === "NewConceptSuggestion" && !useCounterIRI &&
                                                <IRIentry
                                                    title="Concept IRI"
                                                    key={computedIRI || "conceptIRI"}
                                                    margin={false}
                                                    disabled={processingIRI}
                                                    componentIRI={schemeIRI}
                                                    visible={viewIRI}
                                                    startIRI={computedIRI}
                                                    graph={graph}
                                                    resetIRIconfirmation={this.resetIRIconfirmation}
                                                    confirmUniqueIRI={this.confirmUniqueIRI}
                                                    rejectIRI={this.rejectIRI}
                                                />
                                            }
                                            {optionForXL && useXL && !useCounterIRI &&
                                                <React.Fragment>
                                                    <IRIentry
                                                        title="Label IRI"
                                                        dataTest="label-iri-entry-field"
                                                        margin={true}
                                                        visible={viewIRI}
                                                        disabled={processingIRI}
                                                        componentIRI={schemeIRI}
                                                        handleIRIclash={iriOptions.handleIRIclash}
                                                        key={labelComputedIRI || "labelIRI"}
                                                        startIRI={labelComputedIRI}
                                                        graph={graph}
                                                        resetIRIconfirmation={this.resetLabelIRIconfirmation}
                                                        confirmUniqueIRI={this.confirmLabelUniqueIRI}
                                                        rejectIRI={this.rejectLabelIRI}
                                                    />
                                                    {iriTheSame && viewIRI && <Typography data-test="two-iris" variant="caption">The two IRIs must be different.</Typography>}
                                                </React.Fragment>
                                            }
                                        </div>
                                    }
                                </div>
                            }
                        </form>
                    </DialogContent>
                    <DialogActions className={classes.dialogActions}>
                        <CancelButton dataTest="cancel-note" onClick={this.cancelAddNote}>Cancel</CancelButton>
                        <PrimaryButton
                            dataTest="add-suggestion-note"
                            disabled={iriRejected || labelIriRejected || invalid}
                            onClick={this.addNote}>{iriRejected || labelIriRejected ? "Check IRI" : "Confirm"}</PrimaryButton>
                    </DialogActions>
                </Dialog>
            </React.Fragment>
        );
    }
}

AddSuggestionNoteDialog.defaultProps = {
    optionForXL: false,
    useXL: false,
    accepted: false,
    iriOptions: {}
};

AddSuggestionNoteDialog.propTypes = {
    optionForXL: PropTypes.bool,
    useXL: PropTypes.bool,
    accepted: PropTypes.bool.isRequired,
    title: PropTypes.string,
    schemeIRI: PropTypes.string,
    defaultLang: PropTypes.string.isRequired,
    labelResourceIRI: PropTypes.string,
    suggestionType: PropTypes.string,
    graph: PropTypes.string,
    useSlashIRI: PropTypes.bool,
    useCounterIRI: PropTypes.bool,
    counters: PropTypes.object,
    resourceIRI: PropTypes.string,
    onAddNote: PropTypes.func.isRequired,
    onCancelNote: PropTypes.func.isRequired,
    classes: PropTypes.object,
    iriOptions: PropTypes.object
};

export default withStyles(styles)(AddSuggestionNoteDialog);